import * as React from 'react';

import {motion} from 'framer-motion';

import {Card} from 'shells/card/card';
import {LatestOutreachListItem} from 'shells/email-latest-outreach-list/latest-outreach-list-item';
import type {Stat} from 'shells/email-latest-outreach-list/types';

import type {
    EditionFragment as DripEdition,
    SmsDripEditionFragment as SmsDripEdition,
    TimespanInput,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';

import {DripSequenceRecipientModal} from '../recipient-modal/drip-sequence-recipient-modal';
import {getQueuedRecipientsText} from '../recipient-modal/helpers/get-queued-recipients-text';

import {DelayBarWrapper} from './delay-bar-wrapper';

type Props = {
    edition: DripEdition | SmsDripEdition;
    firstEditionId: string;
    engagementStats: Stat[];
    refetchDripEditions: () => Promise<any>;
    onEditionUpdate: (
        editionId: string,
        sendDelay?: TimespanInput | null | undefined
    ) => Promise<any>;
    onRemoveEdition?: (id: string) => void;
};

export function DripSequenceEditionsListItem(props: Props) {
    const [isShowingRecipientModal, setIsShowingRecipientModal] = React.useState<boolean>(false);
    const [isRemovingItem, setIsRemovingItem] = React.useState<boolean>(false);

    const {edition, firstEditionId, onEditionUpdate, refetchDripEditions, onRemoveEdition} = props;

    const recipientCountText =
        edition.status === 'SENDING' ||
        edition.status === 'RETIRING' ||
        (edition.status === 'PAUSED' && edition.queuedDripProgressions)
            ? // @ts-expect-error
              getQueuedRecipientsText(edition.queuedDripProgressions.totalCount)
            : undefined;
    const isFirstEdition = edition.id === firstEditionId;

    return (
        <React.Fragment>
            <>
                <motion.div
                    initial='visible'
                    exit='hidden'
                    variants={{visible: {opacity: 1}, hidden: {opacity: 0}}}
                    animate={isRemovingItem ? 'hidden' : 'visible'}
                    transition={{duration: 0.7}}
                >
                    {isFirstEdition && (
                        <DelayBarWrapper
                            edition={edition}
                            recipientCountText={recipientCountText}
                            isFirstEdition={isFirstEdition}
                            onEditionUpdate={onEditionUpdate}
                            setIsShowingRecipientModal={setIsShowingRecipientModal}
                            setIsRemovingItem={setIsRemovingItem}
                        />
                    )}
                </motion.div>
                <Card hasBorder={true}>
                    {!isFirstEdition && (
                        <DelayBarWrapper
                            edition={edition}
                            recipientCountText={recipientCountText}
                            isFirstEdition={isFirstEdition}
                            onEditionUpdate={onEditionUpdate}
                            setIsShowingRecipientModal={setIsShowingRecipientModal}
                            setIsRemovingItem={setIsRemovingItem}
                        />
                    )}
                    <LatestOutreachListItem
                        // @ts-expect-error
                        edition={{
                            ...edition,
                            engagementStats: props.engagementStats,
                        }}
                        onView={(url) => {
                            appHistory.push(url);
                        }}
                        isDripEdition={true}
                        hasNoBorder={true}
                        archive={
                            edition.status === 'NEW' && onRemoveEdition
                                ? {
                                      onArchive: onRemoveEdition,
                                      shouldConfirm: true,
                                  }
                                : undefined
                        }
                    />
                </Card>
            </>
            {
                // We won't ever show this modal without this text
                isShowingRecipientModal &&
                recipientCountText &&
                edition.queuedDripProgressions &&
                // TODO : Remove this once we make the modal work for SMS
                edition.__typename === 'Edition' ? (
                    <DripSequenceRecipientModal
                        recipientCount={edition.queuedDripProgressions.totalCount}
                        // @ts-expect-error
                        emailTemplateId={edition.emailTemplate.id}
                        editionId={edition.id}
                        title={edition.name}
                        subtitle={recipientCountText}
                        onClose={() => {
                            setIsShowingRecipientModal(false);
                            refetchDripEditions();
                        }}
                    />
                ) : undefined
            }
        </React.Fragment>
    );
}
